import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

const STORAGE_KEY = 'sanin-cart-v2';

function lineKey(item) {
  return item.productId;
}

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setItems(parsed);
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* ignore */
    }
  }, [items]);

  const addItem = useCallback((item) => {
    setItems((prev) => {
      const key = lineKey(item);
      const idx = prev.findIndex((i) => lineKey(i) === key);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          quantity: Math.min(99, next[idx].quantity + item.quantity),
        };
        return next;
      }
      return [...prev, { ...item, quantity: Math.min(99, Math.max(1, item.quantity)) }];
    });
  }, []);

  const removeItem = useCallback((item) => {
    setItems((prev) => prev.filter((i) => lineKey(i) !== lineKey(item)));
  }, []);

  const updateQuantity = useCallback((item, quantity) => {
    const q = Math.min(99, Math.max(1, Number(quantity) || 1));
    setItems((prev) =>
      prev.map((i) => (lineKey(i) === lineKey(item) ? { ...i, quantity: q } : i)),
    );
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const totalCount = useMemo(
    () => items.reduce((n, i) => n + i.quantity, 0),
    [items],
  );

  const hasChairItems = useMemo(
    () => items.some((i) => i.category === 'stolice'),
    [items],
  );

  const value = useMemo(
    () => ({
      items,
      addItem,
      removeItem,
      updateQuantity,
      clearCart,
      totalCount,
      hasChairItems,
    }),
    [items, addItem, removeItem, updateQuantity, clearCart, totalCount, hasChairItems],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) {
    throw new Error('useCart mora biti unutar CartProvider');
  }
  return ctx;
}
